import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { VoicemailMessage, VoicemailMessageCatalogue } from 'src/app/models/voicemail-message.model';

/**
 * Reads the pre-recorded voicemail catalogue that fills the call dialog's message select.
 *
 * The path is four segments deep because Firestore ORs its rules: a single-segment doc under
 * welby_configuration stays writable by any signed-in account through the generic rule there, however
 * strict a narrower block is. See firestore.rules.
 *
 * A failure to read resolves to an empty list rather than an error: the select's default is to
 * leave a live message, which is what staff did before this feature existed, so an unreadable
 * catalogue costs the option to pre-record and nothing else. Blocking the call dialog over it
 * would be a far worse trade.
 */
@Injectable({ providedIn: 'root' })
export class VoicemailMessagesService {
  private messages$: Observable<VoicemailMessage[]> | null;

  constructor(private firestore: AngularFirestore) {}

  /** Active catalogue entries, in display order. */
  getActiveMessages(): Observable<VoicemailMessage[]> {
    if (!this.messages$) {
      this.messages$ = this.firestore
        .doc('welby_configuration/voicemail/messages/catalogue')
        .get()
        .pipe(
          map((snap) => (snap.exists ? ((snap.data() as VoicemailMessageCatalogue)?.messages ?? []) : [])),
          map((messages) => messages.filter((message) => message?.isActive)),
          map((messages) => [...messages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))),
          shareReplay(1),
          // catchError sits *after* shareReplay so a failed read is not what gets cached. Cached,
          // one blip during a token refresh would leave every dialog in the tab showing no
          // pre-recorded messages until a full page reload, with nothing on screen to say why.
          catchError(() => {
            this.messages$ = null;
            return of([] as VoicemailMessage[]);
          })
        );
    }
    return this.messages$;
  }
}
